import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>The core of electroacoustic transducers [1, 2, 3] is the conversion of energy between the electrical and mechanical fields. The question is: how to transform an electrical signal into mechanical motion and vice versa? To achieve this, different types of transduction processes based on various physical laws are used. Here are the most used in electroacoustics:
<ul class=itemize1>
   <li class=itemize>the <strong><span class=cmbx-10>electrodynamic</span></strong> transduction is the most popular and is used in many loudspeaker or headphone systems as well as in recording microphones,
   <li class=itemize>the <strong><span class=cmbx-10>electrostatic</span></strong> conversion is mainly used for microphones, although hifi loudspeakers or headphones using this technology are also available on the market. It also has the advantage of being very easily miniaturised and is therefore widely used in microphones for consumer electronics,
   <li class=itemize>the <strong><span class=cmbx-10>piezoelectric</span></strong> effect is widely used in accelerometers and force sensors. It is also the most common principle for sources and receivers used in ultrasonic applications,
   <li class=itemize>the <strong><span class=cmbx-10>electromagnetic</span></strong> conversion is mainly used for hearing aid loudspeakers or musical instrument sensors,
   <li class=itemize>the <strong><span class=cmbx-10>electroresistive</span></strong> and <strong><span class=cmbx-10>magnetostrictive</span></strong> principles also exist but are less commonly used than the principles mentioned above.
</ul>
<p>This Lesson focuses on the electrodynamic principle.
<h3 class=sectionHead>Electrodynamic mechanism</h3>
<p>
   Let us first recall two electromagnetism laws on which the electrodynamic coupling is based. When a current
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
   </math>
   is flowing in a conductor of total length
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ℓ</mi>
   </math>
   placed in a magnetic field
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>, a Lorentz force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mstyle mathvariant=bold>
         <msub>
            <mrow>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>L</mi>
            </mrow>
         </msub>
      </mstyle>
   </math>
   is applied to the moving charges in the element
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>dℓ</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>d</mi>
            <mstyle mathvariant=bold>
               <msub>
                  <mrow>
                     <mi>F</mi>
                  </mrow>
                  <mrow>
                     <mi>L</mi>
                  </mrow>
               </msub>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mstyle mathvariant=bold>
               <mi>I</mi>
            </mstyle>
            <mi mathvariant=italic>dℓ</mi>
            <mo class=MathClass-bin>×</mo>
            <mstyle mathvariant=bold>
               <mi>B</mi>
            </mstyle>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>×</mo>
   </math>
   is the vector product. The norm of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mstyle mathvariant=bold>
         <msub>
            <mrow>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>L</mi>
            </mrow>
         </msub>
      </mstyle>
   </math>
   is given by
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>d</mi>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>BIdℓ</mi>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ϕ</mi>
   </math>
   is the angle between vectors
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>. Note that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
   </math>
   is null for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
      <mo class=MathClass-rel>∥</mo>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   and that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mstyle mathvariant=bold>
         <msub>
            <mrow>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>L</mi>
            </mrow>
         </msub>
      </mstyle>
   </math>
   is perpendicular to the plane formed by vectors
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   (right hand law). When
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
      <mo class=MathClass-rel>⊥</mo>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   (see <a href="#" data-img-alt="Figure 1. Lorentz force generation." data-img-src=${config.ASSETS_URL}/04/09/theory/figure-1.svg>Figure 1</a>) the Lorentz force is maximum and is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>BIdℓ</mi>
   </math>
   .<br class=newline>If
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>
   is constant over all elements
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>dℓ</mi>
   </math>, the magnitude of the total Lorentz force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
   </math>
   is then equal to
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
            </msub>
            <mi mathvariant=italic>BIdℓ</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>BℓI</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   Finally, we obtain a resultant mechanical force that is proportional to the current supplied. This physical process is used in loudspeakers for which the Lorentz force drives the diaphragm into vibration.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 1. Lorentz force generation." src=${config.ASSETS_URL}/04/09/theory/figure-1.svg><figcaption class=caption>Figure 1. Lorentz force generation.</figcaption></figure>
<p>
   Now, let’s look at another property of electromagnetism: if a conductor is moving in a magnetic field
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   with a velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>V</mi>
      </mstyle>
   </math>
   (see <a href="#" data-img-alt="Figure 2. Electromotive force generation." data-img-src=${config.ASSETS_URL}/04/09/theory/figure-2.svg>Figure 2</a>), a motional ElectroMotive Force (EMF)
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>de</mi>
   </math>
   is induced according to the Maxwell–Faraday equation
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi mathvariant=italic>de</mi>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mstyle mathvariant=bold>
                     <mi>V</mi>
                  </mstyle>
                  <mo class=MathClass-bin>×</mo>
                  <mstyle mathvariant=bold>
                     <mi>B</mi>
                  </mstyle>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-punc>.</mo>
            <mi>d</mi>
            <mstyle mathvariant=bold-italic>
               <mi>ℓ</mi>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>BV</mi>
            <mi mathvariant=italic>dℓ</mi>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>ψ</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ψ</mi>
   </math>
   is the angle between vectors
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>V</mi>
      </mstyle>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>. Note that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>de</mi>
   </math>
   is null for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ψ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>de</mi>
   </math>
   is maximum when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>V</mi>
      </mstyle>
      <mo class=MathClass-rel>⊥</mo>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>ψ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>). Then, for a constant value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>B</mi>
   </math>
   over
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ℓ</mi>
   </math>, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>V</mi>
      </mstyle>
      <mo class=MathClass-rel>⊥</mo>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>, the total EMF is given by
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>e</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
            </msub>
            <mi mathvariant=italic>BV</mi>
            <mi mathvariant=italic>dℓ</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>BℓV</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Although the name used is that of an electromotive force,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>e</mi>
   </math>
   corresponds to an electrical voltage.<br class=newline>This last effect is used in electrodynamic microphones: the sound wave drives a conductor into vibration. As this conductor moves in a magnetic field, an EMF is created across its terminals.
   <figure class=figure>
<p><img alt="Figure 2. Electromotive force generation." src=${config.ASSETS_URL}/04/09/theory/figure-2.svg><figcaption class=caption>Figure 2. Electromotive force generation.</figcaption></figure>
<h3 class=sectionHead>Coupling equation</h3>
<p>The equations giving the Lorentz force and motional EMF are the coupled equations that govern the behaviour of electrodynamic transducers
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis equalrows=false style="" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>e</mi>
                           <mo class=MathClass-rel>=</mo>
                           <mi mathvariant=italic>BℓV</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>L</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mi mathvariant=italic>BℓI</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   It can be noted that each equation in this system includes both mechanical and electrical terms which are linked by the force factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>. An electrical analogy can be used to represent these coupling equations by using a gyrator [4] that couples the electrical and mechanical parts (see <a href="#" data-img-alt="Figure 3. Gyrator as electrical analogy for the electrodynamic transduction." data-img-src=${config.ASSETS_URL}/04/09/theory/figure-3.svg>Figure 3</a>). Compared to the purely electrical gyrator, this one uses a gyration ratio that have the dimension of a resistance but which represents the electro-mechanical coupling. This two-port device will be used in future Lessons to build the electrical circuits equivalent to electrodynamic transducers as it links the mechanical end electrical parts of the transducers to model.
   <figure class=figure>
<p><img alt="Figure 3. Gyrator as electrical analogy for the electrodynamic transduction." src=${config.ASSETS_URL}/04/09/theory/figure-3.svg><figcaption class=caption>Figure 3. Gyrator as electrical analogy for the electrodynamic transduction.</figcaption></figure>
<h3 class=sectionHead>Geometries used in audio transducers</h3>
<p>
   In the previous section, we saw that the electrodynamic coupling is optimal for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
      <mo class=MathClass-rel>⊥</mo>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>V</mi>
      </mstyle>
      <mo class=MathClass-rel>⊥</mo>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>. To meet these constraints, transducer manufacturers use two main designs: the moving ribbon and the moving coil. The first consists of an electrically conductive ribbon placed between the poles of a magnet, while the second uses a cylindrical voice coil which is placed in a gap between the poles of a magnetic system. <a href="#" data-img-alt="Figure 4. Moving ribbon transducer." data-img-src=${config.ASSETS_URL}/04/09/theory/figure-4.svg>Figure 4</a> shows a schematic diagram of a moving ribbon transducer. It can be seen that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>V</mi>
      </mstyle>
   </math>
   are perpendicular to each other.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 4. Moving ribbon transducer." src=${config.ASSETS_URL}/04/09/theory/figure-4.svg><figcaption class=caption>Figure 4. Moving ribbon transducer.</figcaption></figure>
<p>
   The principle of a moving coil transducer is shown in <a href="#" data-img-alt="Figure 5. Cut view of the motor of a moving coil transducer." data-img-src=${config.ASSETS_URL}/04/09/theory/figure-5.svg>Figure 5</a>. Here also,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>I</mi>
      </mstyle>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle mathvariant=bold>
         <mi>V</mi>
      </mstyle>
   </math>
   are perpendicular to each other.
   <figure class=figure>
<p><img alt="Figure 5.Cut view of the motor of a moving coil transducer." src=${config.ASSETS_URL}/04/09/theory/figure-5.svg><figcaption class=caption><span class=id>Figure 5. Cut view of the motor of a moving coil transducer.</figcaption></figure>
<h3 class=sectionHead>Reciprocity of the transduction process</h3>
<p>The electrodynamic principle is a reciprocal transduction process, so it can be used for both microphones and loudspeakers:
<ul class=itemize1>
   <li class=itemize>
      for a microphone, the incoming acoustic wave causes the membrane and the attached voice coil to vibrate with a velocity
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mstyle mathvariant=bold>
            <mi>V</mi>
         </mstyle>
      </math>. The movement creates an induced EMF
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>e</mi>
      </math>
      and therefore an induced current
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>I</mi>
      </math>
      in the conductor. Then,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>I</mi>
      </math>
      in turn creates a Lorentz force
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>L</mi>
            </mrow>
         </msub>
      </math>
      opposing the initial movement. This last result is typical of induction phenomena that always oppose to the cause that created them,
   <li class=itemize>
      for a loudspeaker, the voice coil is supplied with a voltage which causes a current
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>I</mi>
      </math>
      to flow through the conductor in the magnetic field
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>B</mi>
      </math>. The current creates a Lorentz force, the conductor will then vibrate with a velocity
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>V</mi>
      </math>
      in the same direction as the Lorentz force. The moving conductor is then subjected to an induced EMF which opposes to the current
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>I</mi>
      </math>.
</ul>
<p>This reciprocal phenomenon that causes opposition to the excitation (either mechanical or electrical) is a beneficial property, as it damps the mechanical oscillation of the resonant system. In other words, the vibration of the diaphragm will not be perpetuated, hence avoiding long ringing artefacts.
<h3 class=sectionHead>Conclusion</h3>
<p>In future Lessons, we will use electrodynamic coupling to model the behaviour of transducers using either the coupling equations or the gyrator as an electrical analogy of the electrodynamic transduction.
<h3 class=likesectionHead>Glossary</h3>
<p>
  <dl>
    <dt>EMF
    <dd>ElectroMotive Force
  </dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Beranek2012>[1]
   <dd class=thebibliography id=bib-1>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780123914866.
   <dt class=thebibliography id=X0-Kleiner2013>[2]
   <dd class=thebibliography id=bib-2>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780429073519.
   <dt class=thebibliography id=X0-Rossi1988>[3]
   <dd class=thebibliography id=bib-3>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
   <dt class=thebibliography id=X0-tellegen1948gyrator>[4]
   <dd class=thebibliography id=bib-4>
      <p>Bernard DH Tellegen. “The gyrator, a new electric network element”. In: <span class=cmti-10>Philips Res. Rep </span>3.2 (1948), pp. 81–101.
</dl>`;

export default theory;
